import { emptyQuillContent, updateValidationSchema } from 'domain/Updates/shared/validationSchema';
import { useFormik } from 'formik';
import { CompanyUpdateFormValues } from '../../company/types';
import useFileUpload from 'domain/shared/Files/Upload/useFileUpload';
import usePrompt from 'hooks/usePrompt';
import { uploadFile } from 'domain/shared/Files/actions';
import DocumentAPI from 'apis/DocumentAPI';
import i18n from 'util/i18n';
import { DocumentList } from 'types/documents';
import { stripUpdateEmbeds } from 'ui/elements/form/RichTextEditor/components/UpdateBlot';
import { stripKpiEmbeds } from 'ui/elements/form/RichTextEditor/components/KpiBlot';

export function useUpdateForm(props: {
  onSave: (dto: CompanyUpdateFormValues) => void;
  titleDraft?: string;
  contentDraft?: string;
  bannerImage?: string;
  attachments?: DocumentList;
  linkPreview?: string;
  topicId?: string;
}) {
  const formik = useFormik<CompanyUpdateFormValues>({
    initialValues: {
      title: props.titleDraft ?? undefined,
      category: 'News',
      bannerImage: props.bannerImage || '',
      content: props.contentDraft ?? emptyQuillContent,
      structuredContent: {} as string,
      attachments: props.attachments?.documents.map(doc => doc.id) || [],
      linkPreview: props.linkPreview,
      topicId: props.topicId,
    },
    onSubmit: values => {
      const html = values.content;
      const cleanedUpdates = stripUpdateEmbeds(html);
      const cleanedKpis = stripKpiEmbeds(cleanedUpdates);

      // Parse structuredContent if it's a string
      const structuredContentObj =
        typeof values.structuredContent === 'string'
          ? JSON.parse(values.structuredContent || '{}')
          : values.structuredContent || {};

      const data: CompanyUpdateFormValues = {
        attachments: values.attachments,
        title: values.title,
        category: values.category,
        bannerImage: values.bannerImage,
        content: cleanedKpis,
        structuredContent: JSON.stringify({
          ...structuredContentObj,
        }),
        linkPreview: values.linkPreview,
        topicId: values.topicId,
      };

      props.onSave(data);
    },
    validationSchema: updateValidationSchema,
    validateOnChange: false,
    validateOnBlur: true,
  });
  const onAttachmentUploaded = (files: { documentId: string; name: string }[]) => {
    const newDocumentIds = files.map(f => f.documentId);
    formik.setFieldValue('attachments', [...formik.values.attachments, ...newDocumentIds]);
  };

  const onAttachmentRemoved = (documentId: UUID): Promise<void> => {
    DocumentAPI.userFiles.deleteFile(documentId);
    return Promise.resolve(
      formik.setFieldValue(
        'attachments',
        formik.values.attachments.filter(doc => doc !== documentId),
      ),
    ).then(() => {});
  };

  // Check if there are meaningful changes (not just form initialization)
  // Strip HTML tags to get actual text content (ignores styling changes like class additions)
  const getTextContent = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const currentText = getTextContent(formik.values.content).trim();
  const initialText = getTextContent(props.contentDraft ?? emptyQuillContent).trim();
  const hasContentChanged = currentText.length > 0 && currentText !== initialText;

  const initialAttachmentIds = props.attachments?.documents.map(doc => doc.id) || [];
  const hasAttachmentsChanged =
    formik.values.attachments.length !== initialAttachmentIds.length ||
    formik.values.attachments.some(id => !initialAttachmentIds.includes(id));

  const hasRealChanges = hasContentChanged || hasAttachmentsChanged;

  usePrompt(hasRealChanges, formik.isSubmitting);
  const [newAttachments, onUploadFile, removeFile] = useFileUpload(file => uploadFile('updates', file), {
    onSuccess: onAttachmentUploaded,
    deleteFile: onAttachmentRemoved,
  });

  const deleteAttachments = (attachmentIdsToKeep: string[], allAttachmentIds: string[]) => {
    const attachmentsToDelete = allAttachmentIds.filter(att => !attachmentIdsToKeep.includes(att));
    attachmentsToDelete?.forEach(documentId => {
      DocumentAPI.userFiles.deleteFile(documentId);
    });
  };

  const onCancel = (callback: () => void) => {
    if (hasRealChanges && !formik.isSubmitting) {
      const confirmed = confirm(i18n('en').common.unsavedChangesPrompt);
      if (confirmed) {
        deleteAttachments(props.attachments?.documents.map(d => d.id) ?? [], formik.values.attachments);
        callback();
      }
    } else {
      callback();
    }
  };

  const onSubmit = () => {
    deleteAttachments(formik.values.attachments, props.attachments?.documents.map(d => d.id) ?? []);
    formik.handleSubmit();
  };

  return { formik, newAttachments, onAttachmentRemoved, onUploadFile, removeFile, onCancel, onSubmit, hasRealChanges };
}
